import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Trash2, Mail, ShieldCheck, Copy, CheckCircle2, AlertTriangle } from "lucide-react";

export default function EmailDomains() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ domain: "", from_name: "", from_email: "" });
  const [dnsFor, setDnsFor] = useState(null);

  const load = () => api.get("/email-domains").then(r => setItems(r.data));
  useEffect(() => { load(); }, []);

  const create = async () => {
    try { const { data } = await api.post("/email-domains", form); toast.success("Domain added"); setDnsFor(data); setOpen(false); setForm({ domain: "", from_name: "", from_email: "" }); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const verify = async (id) => {
    try { const { data } = await api.post(`/email-domains/${id}/verify`); toast.success(`Verified: SPF ✓ DKIM ✓ DMARC ✓`); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Verify failed"); }
  };
  const del = async (id) => { if (!window.confirm("Remove this email domain?")) return; await api.delete(`/email-domains/${id}`); load(); };
  const copy = (t) => { navigator.clipboard.writeText(t); toast.success("Copied"); };

  const DnsPanel = ({ d }) => (
    <Dialog open={!!d} onOpenChange={(v) => !v && setDnsFor(null)}>
      <DialogContent className="rounded-sm max-w-3xl">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><ShieldCheck className="h-4 w-4" /> Add these DNS records at your registrar</DialogTitle><DialogDescription>Once the records propagate (usually within a few minutes), press <b>Verify</b> below.</DialogDescription></DialogHeader>
        <div className="space-y-3 text-xs">
          <div className="border border-border rounded-sm p-3 space-y-2">
            <div className="flex items-center justify-between"><b>SPF</b><Badge variant="outline" className="rounded-sm text-[10px]">TXT</Badge></div>
            <div className="text-muted-foreground">Host: <code className="font-mono">@</code> or <code className="font-mono">{d?.domain}</code></div>
            <div className="flex items-center gap-2"><code className="flex-1 font-mono bg-background border border-border rounded-sm p-2 break-all">{d?.spf_record}</code>
              <Button size="icon" variant="outline" className="h-8 w-8 rounded-sm" onClick={() => copy(d?.spf_record)}><Copy className="h-3.5 w-3.5" /></Button></div>
          </div>
          <div className="border border-border rounded-sm p-3 space-y-2">
            <div className="flex items-center justify-between"><b>DKIM</b><Badge variant="outline" className="rounded-sm text-[10px]">TXT</Badge></div>
            <div className="text-muted-foreground">Host: <code className="font-mono">{d?.dkim_selector}._domainkey</code></div>
            <div className="flex items-center gap-2"><code className="flex-1 font-mono bg-background border border-border rounded-sm p-2 break-all text-[10px]">{d?.dkim_public_key}</code>
              <Button size="icon" variant="outline" className="h-8 w-8 rounded-sm" onClick={() => copy(d?.dkim_public_key)}><Copy className="h-3.5 w-3.5" /></Button></div>
          </div>
          <div className="border border-border rounded-sm p-3 space-y-2">
            <div className="flex items-center justify-between"><b>DMARC</b><Badge variant="outline" className="rounded-sm text-[10px]">TXT</Badge></div>
            <div className="text-muted-foreground">Host: <code className="font-mono">_dmarc.{d?.domain}</code></div>
            <div className="flex items-center gap-2"><code className="flex-1 font-mono bg-background border border-border rounded-sm p-2 break-all">v=DMARC1; p=none; rua=mailto:dmarc@{d?.domain}</code>
              <Button size="icon" variant="outline" className="h-8 w-8 rounded-sm" onClick={() => copy(`v=DMARC1; p=none; rua=mailto:dmarc@${d?.domain}`)}><Copy className="h-3.5 w-3.5" /></Button></div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" className="rounded-sm" onClick={() => setDnsFor(null)}>Close</Button>
          <Button className="rounded-sm gap-2" onClick={async () => { await verify(d.id); setDnsFor(null); }} data-testid={`verify-${d?.id}`}><CheckCircle2 className="h-4 w-4" /> Verify</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );

  return (
    <div className="space-y-4" data-testid="email-domains-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground">Email</div>
          <h1 className="text-3xl font-black tracking-tighter">Email Domains</h1>
          <p className="text-xs text-muted-foreground mt-1">Verify SPF, DKIM &amp; DMARC on your sender domain so transactional emails land in the primary inbox.</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button className="rounded-sm gap-2" data-testid="new-domain-button"><Plus className="h-4 w-4" /> Add Domain</Button></DialogTrigger>
          <DialogContent className="rounded-sm">
            <DialogHeader><DialogTitle>Add Sending Domain</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div><Label>Domain</Label><Input value={form.domain} onChange={e => setForm({ ...form, domain: e.target.value })} placeholder="mail.your-brand.com" className="rounded-sm font-mono" data-testid="domain-input" /></div>
              <div><Label>From Name</Label><Input value={form.from_name} onChange={e => setForm({ ...form, from_name: e.target.value })} placeholder="Your Brand" className="rounded-sm" /></div>
              <div><Label>From Email</Label><Input value={form.from_email} onChange={e => setForm({ ...form, from_email: e.target.value })} placeholder="no-reply@mail.your-brand.com" className="rounded-sm font-mono" data-testid="domain-from-email" /></div>
            </div>
            <DialogFooter><Button onClick={create} className="rounded-sm" data-testid="domain-create-button">Add</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {items.map(d => (
          <Card key={d.id} className="rounded-sm shadow-none" data-testid={`domain-${d.id}`}>
            <CardContent className="p-4 space-y-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2"><Mail className="h-4 w-4 text-muted-foreground" /><span className="font-mono font-bold">{d.domain}</span></div>
                {d.status === "verified"
                  ? <Badge variant="outline" className="rounded-sm text-[10px] border-emerald-300 text-emerald-700 gap-1"><CheckCircle2 className="h-3 w-3" /> VERIFIED</Badge>
                  : <Badge variant="outline" className="rounded-sm text-[10px] border-amber-300 text-amber-700 gap-1"><AlertTriangle className="h-3 w-3" /> PENDING</Badge>}
              </div>
              <div className="text-xs text-muted-foreground">From: <span className="font-mono">{d.from_name ? `${d.from_name} <${d.from_email}>` : d.from_email}</span></div>
              <div className="grid grid-cols-3 gap-2 text-[10px] uppercase tracking-wider">
                <div className={`flex items-center gap-1 ${d.spf_verified ? "text-emerald-700" : "text-muted-foreground"}`}>{d.spf_verified ? <CheckCircle2 className="h-3 w-3" /> : <span>·</span>} SPF</div>
                <div className={`flex items-center gap-1 ${d.dkim_verified ? "text-emerald-700" : "text-muted-foreground"}`}>{d.dkim_verified ? <CheckCircle2 className="h-3 w-3" /> : <span>·</span>} DKIM</div>
                <div className={`flex items-center gap-1 ${d.dmarc_verified ? "text-emerald-700" : "text-muted-foreground"}`}>{d.dmarc_verified ? <CheckCircle2 className="h-3 w-3" /> : <span>·</span>} DMARC</div>
              </div>
              <div className="flex gap-1 justify-end">
                <Button variant="outline" size="sm" className="rounded-sm gap-1" onClick={() => setDnsFor(d)} data-testid={`dns-${d.id}`}><ShieldCheck className="h-3 w-3" /> DNS records</Button>
                <Button variant="ghost" size="sm" className="rounded-sm text-red-600" onClick={() => del(d.id)}><Trash2 className="h-3 w-3" /></Button>
              </div>
            </CardContent>
          </Card>
        ))}
        {items.length === 0 && <Card className="rounded-sm shadow-none md:col-span-2"><CardContent className="text-center text-muted-foreground py-10">No sending domains yet.</CardContent></Card>}
      </div>

      <DnsPanel d={dnsFor} />
    </div>
  );
}
